import * as React from "react";
import { UitkCard, UitkCardMedia } from "uitk-react-cards";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { TopCitiesAffinityCarouselProps } from "../../typings";
import { UitkFigure, UitkImage, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { inject } from "mobx-react";
import { useLocalization } from "@shared-ui/localization-context";
import { ThumborStore } from "stores/thumbor/ThumborStore";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { CityDiscovery } from "src/typings/microserviceModels/top-cities-flex-module";
import IsomorphicCarousel from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { AttributionText } from "components/shared/AttributionText/AttributionText";
import { renderTopCitiesAttributionTextHTML } from "../../utils/renderTopCitiesAttributionTextHTML";
import { UitkHeading } from "uitk-react-text";

export const TopCitiesAffinityCarousel = (props: TopCitiesAffinityCarouselProps) => {
  const flexComponent = props.flexComponent;
  const moduleName = flexComponent.name;
  const keyHelper = new ItemKeyHelper("affinity-top-cities");
  const model = flexComponent.model;
  const affinityCode = props.context.searchContext.affinity?.code;
  const isMobile = props.context.deviceInformation?.mobile;
  const { formatText } = useLocalization();
  const { title } = model;

  return (
    <div
      className="TopCitiesAffinityCarousel uitk-cell all-y-margin-four affinityCarouselContainer"
      id={flexComponent.id}
    >
      <UitkHeading level={2} size={600}>
        {title}
      </UitkHeading>
      <IsomorphicCarousel
        buttonText={{
          nextButton: formatText("carousel.item.next"),
          prevButton: formatText("carousel.item.prev"),
        }}
        itemsVisible={{ lg: 5, md: 3, sm: 1 }}
        pageBy={1}
        peekWidth={Number(isMobile && 12)}
        carouselName={"AffinityDestinations"}
        className="all-t-margin-three"
      >
        {model.topCities.map((city, cityIndex) => (
          <CityCard
            key={keyHelper.next()}
            city={city}
            index={cityIndex}
            context={props.context}
            moduleName={moduleName}
            affinityCode={affinityCode}
          />
        ))}
      </IsomorphicCarousel>
    </div>
  );
};

export const CityCard = inject("thumbor")(
  (props: {
    city: CityDiscovery;
    index: number;
    thumbor?: ThumborStore;
    context: ExtendedContextStore;
    moduleName: string;
    affinityCode: string;
  }) => {
    const { city, context, thumbor, affinityCode } = props;
    const { name, imageUrl, hotelCount, images } = city;
    const cityImageUrl =
      thumbor &&
      imageUrl &&
      thumbor.safelyGenerateThumborizedImageUrl(imageUrl as string, 384, 216, context, undefined, true, 100);
    const { formatText } = useLocalization();

    const image = images && images.length > 0 ? images[0] : undefined;

    return (
      <UitkCard className="uitk-cell uitk-carousel-item">
        <UitkCardMedia>
          <UitkFigure className="destinationImage" ratio={UitkFigureAspectRatioType.R16_9}>
            <TrackedLink
              className="uitk-attribution-link"
              moduleName="topCities"
              href={city.link}
              rfrr="topCities.exploreCityHotels"
              data-testid="destination-image-link"
            >
              {cityImageUrl && <UitkImage alt={name} src={cityImageUrl} placeholderImage={true} />}
              <UitkScrim type={UitkScrimType.BOTTOM} className="destinationImageText">
                <div>
                  <div className="truncate uitk-type-500 uitk-type-bold">{name}</div>
                  <div className="uitk-type-300">
                    <p>{formatText(`topCities.hotels.quantity.${affinityCode}`, hotelCount)}</p>
                  </div>
                  <AttributionText
                    source={image?.source}
                    origin={image?.origin}
                    attribUrl={image?.attribution?.url}
                    authorName={image?.attribution?.authorName}
                    authorUrl={image?.attribution?.authorUrl}
                    licenseSuite={image?.attribution?.license?.suite}
                    licenseName={image?.attribution?.license?.name}
                    licenseUrl={image?.attribution?.license?.url}
                    overrideRender={renderTopCitiesAttributionTextHTML}
                  />
                </div>
              </UitkScrim>
            </TrackedLink>
          </UitkFigure>
        </UitkCardMedia>
      </UitkCard>
    );
  }
);

export default TopCitiesAffinityCarousel;
